import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, getDoc, deleteDoc, collection, addDoc } from "firebase/firestore";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { db } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";

async function cargarPerfil(uid) {
  const { getDoc, doc } = await import("firebase/firestore");
  const ref = doc(db, "usuarios_partes", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

function fmtDate(tsOrDate) {
  try {
    const d =
      tsOrDate?.toDate?.() ||
      (tsOrDate instanceof Date ? tsOrDate : tsOrDate ? new Date(tsOrDate) : null);
    if (!d) return "—";
    return d.toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "—";
  }
}

export default function PartesEliminar() {
  const navigate = useNavigate();
  const { cuerpoSlug } = useCuerpo();
  const partesBase = `/${cuerpoSlug}/partes`;
  const auth = getAuth();
  const [params] = useSearchParams();
  const parteId = params.get("id") || "";

  const [loading, setLoading] = useState(true);
  const [perfil, setPerfil] = useState(null);
  const [uid, setUid] = useState("");

  const [parte, setParte] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  async function logAuditoria({ accion, compania = null, parteId = null, extra = null }) {
    try {
      await addDoc(collection(db, "auditoria_partes"), {
        at: new Date(),
        usuarioId: uid || auth.currentUser?.uid || null,
        usuario: perfil?.usuario || null,
        rol: perfil?.rol || null,
        cuerpo_id: perfil?.cuerpo_id || null,
        compania,
        accion,
        parteId,
        extra,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } catch (e) {
      console.warn("⚠️ Auditoría falló:", e);
    }
  }

  // auth gate
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setPerfil(null);
        setUid("");
        setLoading(false);
        navigate(partesBase);
        return;
      }
      setLoading(true);
      setUid(u.uid);

      const p = await cargarPerfil(u.uid);
      if (!p || p.activo === false) {
        await logAuditoria({ accion: "ACCESS_DENIED_DELETE" });
        await signOut(auth);
        setPerfil(null);
        setLoading(false);
        navigate(partesBase);
        return;
      }

      setPerfil(p);

      // ✅ Solo admin/superadmin pueden eliminar
      const rolNorm = String(p?.rol || "").toLowerCase();
      if (!(rolNorm === "admin" || rolNorm === "superadmin")) {
        await logAuditoria({ accion: "DELETE_DENIED_NOT_ADMIN", parteId });
        alert("❌ Solo Admin / superadmin pueden eliminar partes.");
        navigate(`${partesBase}/lista`);
        return;
      }
      setLoading(false);
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load parte
  useEffect(() => {
    let mounted = true;

    async function cargar() {
      if (!perfil) return;

      if (!parteId) {
        setParte(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const ref = doc(db, "partes_emergencia", parteId);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          if (mounted) setParte(null);
          await logAuditoria({ accion: "DELETE_OPEN_NOT_FOUND", parteId });
          return;
        }

        const data = { id: snap.id, ...snap.data() };

        // permiso por compañía
        if (perfil.rol === "compania" && perfil.compania && data.compania !== perfil.compania) {
          await logAuditoria({ accion: "DELETE_DENIED_WRONG_COMPANY", parteId, extra: { docCompania: data.compania } });
          alert("❌ No tienes permiso para eliminar partes de otra compañía.");
          navigate(`${partesBase}/lista`);
          return;
        }

        if (!mounted) return;

        setParte(data);
        await logAuditoria({ accion: "DELETE_OPEN", compania: data.compania || null, parteId: data.id });
      } catch (e) {
        console.error("❌ Error cargando parte:", e);
        if (mounted) setParte(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    cargar();
    return () => {
      mounted = false;
    };
  }, [perfil, parteId, navigate]);

  const canDelete = useMemo(() => {
    if (!parte) return false;
    if (String(confirmText || "").trim().toUpperCase() !== "ELIMINAR") return false;
    if (!String(password || "").trim()) return false;
    return true;
  }, [parte, confirmText, password]);

  async function eliminar() {
    if (!parte) return;
    if (!canDelete) return;

    const ok = window.confirm(
      `¿Seguro que deseas ELIMINAR este parte?\n\nFolio: ${parte.folio ?? "—"}\nCompañía: ${parte.compania || "—"}\nID: ${parte.id}\n\nEsta acción NO se puede deshacer.`
    );
    if (!ok) return;

    try {
      setDeleting(true);

      // Reautenticación (seguridad extra)
      const user = auth.currentUser;
      if (!user?.email) {
        alert("❌ No se pudo reautenticar (falta email en sesión).");
        return;
      }
      const cred = EmailAuthProvider.credential(user.email, password.trim());
      await reauthenticateWithCredential(user, cred);

      await deleteDoc(doc(db, "partes_emergencia", parte.id));

      await logAuditoria({
        accion: "DELETE_OK",
        compania: parte.compania || null,
        parteId: parte.id,
        extra: { folio: parte.folio ?? null, numeroEmergencia: parte.numeroEmergencia ?? null },
      });

      alert("✅ Parte eliminado.");
      navigate(`${partesBase}/lista`);
    } catch (e) {
      console.error("❌ Error eliminando:", e);
      await logAuditoria({ accion: "DELETE_FAIL", compania: parte?.compania || null, parteId: parte?.id || null, extra: String(e?.code || e?.message || e) });
      alert("❌ No se pudo eliminar. Contraseña incorrecta o error. Revisa consola.");
    } finally {
      setDeleting(false);
      setPassword("");
      setConfirmText("");
    }
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.topbar}>
          <button style={S.btnGhost} onClick={() => navigate(`${partesBase}/lista`)}>
            ← Volver
          </button>
          <div style={{ flex: 1 }}>
            <div style={S.title}>Eliminar Parte</div>
            <div style={S.subtitle}>Colección: partes_emergencia</div>
          </div>
        </div>

        <div style={S.card}>
          {!parteId ? (
            <div style={S.warn}>
              Falta el parámetro <b>?id=</b>. Vuelve a la lista y entra desde “Eliminar”.
            </div>
          ) : loading ? (
            <div style={{ padding: 10 }}>Cargando...</div>
          ) : !parte ? (
            <div style={S.warn}>
              No se encontró el parte con ID: <b>{parteId}</b>
            </div>
          ) : (
            <>
              <div style={S.meta}>
                <div style={{ fontWeight: 900 }}>
                  Folio: {parte.folio ?? "—"} · {parte.compania || "—"}
                </div>
                <div style={{ color: "#334155" }}>
                  <b>Tipo:</b> {parte.tipoEmergenciaNombre || parte.tipoEmergenciaId || "—"}
                  {parte.subTipoEmergenciaNombre ? ` · ${parte.subTipoEmergenciaNombre}` : ""}
                </div>
                <div style={{ color: "#334155" }}>
                  <b>N° Emergencia:</b> {parte.numeroEmergencia || "—"} · <b>Dirección:</b> {parte.direccion || "—"}
                </div>
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  Creado: {fmtDate(parte.createdAt)} · ID: {parte.id}
                </div>
              </div>

              <div style={S.dangerBox}>
                <div style={{ fontWeight: 900, marginBottom: 6 }}>Confirmación obligatoria</div>
                <div style={{ color: "#7f1d1d", marginBottom: 10 }}>
                  Para eliminar escribe <b>ELIMINAR</b> y luego ingresa tu contraseña.
                </div>

                <label style={S.label}>Escribe ELIMINAR</label>
                <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} style={S.input} />

                <label style={S.label}>Contraseña</label>
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={S.input}
                  type="password"
                  placeholder="••••••••"
                />
              </div>

              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <button style={S.btnGhostWhite} onClick={() => navigate(`${partesBase}/lista`)}>
                  Cancelar
                </button>
                <button
                  style={{
                    ...S.btnDanger,
                    opacity: deleting || !canDelete ? 0.6 : 1,
                    cursor: deleting || !canDelete ? "not-allowed" : "pointer",
                  }}
                  onClick={eliminar}
                  disabled={deleting || !canDelete}
                >
                  {deleting ? "Eliminando..." : "Eliminar definitivamente"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: "#0b1220", padding: 14 },
  container: { width: "min(1100px, 96vw)", margin: "0 auto" },
  topbar: { display: "flex", alignItems: "center", gap: 10, color: "white", flexWrap: "wrap", marginBottom: 12 },
  title: { fontSize: 18, fontWeight: 900 },
  subtitle: { opacity: 0.85, fontSize: 13 },
  card: { background: "white", borderRadius: 14, padding: 14, boxShadow: "0 10px 30px rgba(0,0,0,.25)", marginBottom: 12 },
  warn: { padding: 12, background: "#fff7ed", border: "1px solid #fdba74", color: "#7c2d12", borderRadius: 10 },
  meta: { padding: 12, border: "1px solid #e5e7eb", borderRadius: 12, background: "#fafafa" },
  dangerBox: { marginTop: 12, padding: 12, borderRadius: 12, border: "1px solid #fecaca", background: "#fff1f2" },
  label: { display: "block", marginTop: 10, marginBottom: 6, fontWeight: 900, color: "#7f1d1d" },
  input: { width: "100%", padding: 10, borderRadius: 10, border: "1px solid #e5e7eb", outline: "none", boxSizing: "border-box" },
  btnGhost: { padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,.25)", background: "rgba(255,255,255,.08)", color: "white", cursor: "pointer", fontWeight: 800 },
  btnGhostWhite: { padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb", background: "white", cursor: "pointer", fontWeight: 900 },
  btnDanger: { padding: "10px 12px", borderRadius: 10, border: "none", background: "#dc2626", color: "white", fontWeight: 900 },
};
